<script setup lang="ts">
/**
 * IconRenderer — renders an icon from a namespaced string.
 *
 * Formats:
 *   "pi:calendar"      → <i class="pi pi-calendar" />
 *   "tabler:calendar"  → <IconCalendar /> (from @tabler/icons-vue, dynamic import)
 *   ""                 → nothing
 */
import { computed, shallowRef, watch, type Component } from 'vue'

const props = defineProps<{
  icon: string
  class?: string
  size?: number
}>()

function toPascalCase(name: string): string {
  return name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('')
}

const pack = computed(() => {
  if (!props.icon) return null
  const [ns] = props.icon.split(':')
  return ns
})

const iconName = computed(() => {
  if (!props.icon) return ''
  return props.icon.split(':').slice(1).join(':')
})

const tablerComponent = shallowRef<Component | null>(null)

watch(
  [pack, iconName],
  ([p, name]) => {
    if (p !== 'tabler' || !name) {
      tablerComponent.value = null
      return
    }
    const componentName = `Icon${toPascalCase(name)}`
    import('@tabler/icons-vue').then((m) => {
      tablerComponent.value = (m as Record<string, Component>)[componentName] ?? null
    })
  },
  { immediate: true },
)
</script>

<template>
  <i
    v-if="pack === 'pi'"
    :class="['pi', `pi-${iconName}`, props.class]"
  />
  <component
    v-else-if="pack === 'tabler' && tablerComponent"
    :is="tablerComponent"
    :size="size ?? 18"
    :stroke-width="1.5"
    :class="props.class"
  />
</template>
